import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import React from 'react'
import * as RechartsPrimitive from 'recharts'
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent'

import Typography from '../../atoms/typography'
import { getPayloadConfigFromPayload, GraphData, useChart } from './utils'

export const ChartTooltip = RechartsPrimitive.Tooltip

const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof RechartsPrimitive.Tooltip<ValueType, NameType>> &
    React.ComponentProps<'div'> & {
      hideLabel?: boolean
      indicator?: 'line' | 'dot' | 'dashed'
      labelKey?: string
      data: GraphData[]
    }
>(
  (
    {
      active,
      payload,
      className,
      indicator = 'dot',
      hideLabel = false,
      label,
      labelFormatter,
      labelClassName,
      formatter,
      labelKey,
      data
    },
    ref
  ) => {
    const { config } = useChart()

    const total = data.reduce((acc, item) => acc + item.value, 0)

    const tooltipLabel = React.useMemo(() => {
      if (hideLabel || !payload?.length) {
        return null
      }

      const [item] = payload
      const key = `${labelKey || item.dataKey || item.name || 'value'}`
      const itemConfig = getPayloadConfigFromPayload(config, item, key)
      const value =
        !labelKey && typeof label === 'string' ? config[label]?.label || label : itemConfig?.label

      if (labelFormatter) {
        return (
          <div className={cn('font-medium', labelClassName)}>{labelFormatter(value, payload)}</div>
        )
      }

      if (!value) {
        return null
      }

      return <div className={cn('font-medium', labelClassName)}>{value}</div>
    }, [label, labelFormatter, payload, hideLabel, labelClassName, config, labelKey])

    if (!active || !payload?.length) {
      return null
    }

    const nestLabel = payload.length === 1 && indicator !== 'dot'

    return (
      <div
        ref={ref}
        className={cn(
          'grid w-[108px] items-start gap-1.5 rounded-sm bg-white-hover px-2.5 py-1.5 shadow-xl text-primary',
          className
        )}
      >
        <Typography
          variant={'label-medium'}
          className={'first-letter:uppercase'}
          color={tailwindColors.grey.light.active}
        >
          {!nestLabel ? tooltipLabel : ''}
        </Typography>

        <div className={'grid gap-1.5'}>
          {payload.map((item, index) => {
            const typedItem = item
            const typedPayload = typedItem.payload as GraphData
            return (
              <div
                key={typedItem.dataKey}
                className={cn(
                  'flex w-full items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground',
                  indicator === 'dot' && 'items-center'
                )}
              >
                {formatter && typedItem.value && typedItem.name
                  ? formatter(typedItem.value, typedItem.name, typedItem, index, payload)
                  : typedItem.value && (
                      <Typography
                        variant={'label-semibold'}
                        style={{
                          color: typedPayload?.hoverFill || 'inherit' // Fallback to default if no fill
                        }}
                      >
                        {`${((Number(typedItem.value) / total) * 100).toFixed(0)}% (${typedItem.value && typedItem.value.toLocaleString()})`}
                      </Typography>
                    )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }
)

export default ChartTooltipContent
